// ticketDetails.ts - Extended types for SOC Ticket Details
// Based on the 12 categories defined for SOCmate's investigation workflow

import { User, TicketStatus, TicketPriority, TicketCategory } from './index';

// ============================================================================
// ENUMS
// ============================================================================

export enum DetectionMethod {
  SIGNATURE = 'signature',
  BEHAVIORAL = 'behavioral',
  ANOMALY = 'anomaly',
  HEURISTIC = 'heuristic',
  ML_BASED = 'ml_based',
  MANUAL = 'manual'
}

export enum VendorSeverity {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
  INFORMATIONAL = 'informational'
}

export enum AssetType {
  ENDPOINT = 'endpoint',
  SERVER = 'server',
  CLOUD_INSTANCE = 'cloud_instance',
  IOT = 'iot',
  NETWORK_DEVICE = 'network_device',
  MOBILE = 'mobile',
  VIRTUAL_MACHINE = 'virtual_machine'
}

export enum AssetCriticality {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low'
}

export enum NetworkDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
  LATERAL = 'lateral',
  INTERNAL = 'internal'
}

export enum IOCType {
  IP = 'ip',
  DOMAIN = 'domain',
  URL = 'url',
  HASH_MD5 = 'hash_md5',
  HASH_SHA1 = 'hash_sha1',
  HASH_SHA256 = 'hash_sha256',
  FILE_NAME = 'file_name',
  FILE_PATH = 'file_path',
  REGISTRY_KEY = 'registry_key',
  EMAIL = 'email',
  USER_AGENT = 'user_agent'
}

export enum TimelineEventType {
  ALERT = 'alert',
  LOGIN = 'login',
  NETWORK = 'network',
  FILE = 'file',
  PROCESS = 'process',
  MALWARE = 'malware',
  ANALYST_NOTE = 'analyst_note',
  SYSTEM = 'system',
  REMEDIATION = 'remediation'
}

export enum AccountStatus {
  ACTIVE = 'active',
  DISABLED = 'disabled',
  FLAGGED = 'flagged',
  LOCKED = 'locked'
}

export enum PrivilegeLevel {
  ADMIN = 'admin',
  STANDARD = 'standard',
  SERVICE_ACCOUNT = 'service_account',
  GUEST = 'guest',
  PRIVILEGED = 'privileged'
}

// ============================================================================
// CATEGORY 1: TICKET META
// ============================================================================

export interface TicketMeta {
  id: string;
  created_at: string;
  updated_at: string;
  status: TicketStatus;
  assignee: User | null;
  assigned_team: string | null;
  priority: TicketPriority;
  sla_tier: string | null;
  sla_due_at: string | null;
  sla_remaining_minutes: number | null;
  source_system: string;
  category: TicketCategory;
}

// ============================================================================
// CATEGORY 2: ALERT CORE
// ============================================================================

export interface AlertCore {
  alert_id: string;
  alert_name: string;
  rule_name: string | null;
  alert_type: string;
  vendor_severity: VendorSeverity;
  socmate_risk_score: number; // 0-100
  detection_timestamp: string;
  detection_method: DetectionMethod;
  description: string | null;
  risk_score_reason: string | null; // Explains why SOCmate score differs from vendor
}

// ============================================================================
// CATEGORY 3: AFFECTED IDENTITY
// ============================================================================

export interface AffectedIdentity {
  username: string;
  account_name: string | null;
  user_role: string | null;
  privilege_level: PrivilegeLevel;
  department: string | null;
  business_unit: string | null;
  email: string | null;
  account_status: AccountStatus;
  last_login: string | null;
  risk_indicators: string[];
}

// ============================================================================
// CATEGORY 4: AFFECTED ASSET
// ============================================================================

export interface AffectedAsset {
  hostname: string;
  fqdn: string | null;
  ip_address: string;
  mac_address: string | null;
  asset_type: AssetType;
  os: string | null;
  os_version: string | null;
  platform: string | null;
  criticality: AssetCriticality;
  owner: string | null;
  responsible_team: string | null;
  network_segment: string | null;
  vlan: string | null;
  location: string | null;
  tags: string[];
}

// ============================================================================
// CATEGORY 5: NETWORK CONTEXT
// ============================================================================

export interface NetworkContext {
  source_ip: string | null;
  source_port: number | null;
  destination_ip: string | null;
  destination_port: number | null;
  protocol: string | null;
  direction: NetworkDirection;
  bytes_sent: number | null;
  bytes_received: number | null;
  // Geolocation for external IPs
  source_geo: GeoLocation | null;
  destination_geo: GeoLocation | null;
  // DNS
  dns_query: string | null;
  dns_response: string | null;
  // Additional context
  connection_state: string | null;
  session_duration_ms: number | null;
}

export interface GeoLocation {
  country: string | null;
  country_code: string | null;
  city: string | null;
  region: string | null;
  asn: string | null;
  asn_org: string | null;
  is_vpn: boolean;
  is_tor: boolean;
  is_proxy: boolean;
}

// ============================================================================
// CATEGORY 6: MITRE ATT&CK MAPPING
// ============================================================================

export interface MitreMapping {
  tactic: string;
  tactic_id: string;
  technique_id: string;
  technique_name: string;
  sub_technique_id: string | null;
  sub_technique_name: string | null;
  confidence: number; // 0-100
  mitre_url: string | null;
}

// ============================================================================
// CATEGORY 7: IOCs (INDICATORS OF COMPROMISE) - Variable
// ============================================================================

export interface IOC {
  id: string;
  type: IOCType;
  value: string;
  confidence_score: number; // 0-100
  first_seen: string | null;
  last_seen: string | null;
  source: string | null;
  tags: string[];
  // Enrichment data (may be populated from threat intel)
  is_malicious: boolean | null;
  reputation_score: number | null;
  related_campaigns: string[];
}

// ============================================================================
// CATEGORY 8: THREAT INTELLIGENCE ENRICHMENT - Variable
// ============================================================================

export interface ThreatIntelEnrichment {
  threat_actor: string | null;
  apt_group: string | null;
  intel_source: string;
  feed_name: string | null;
  campaign_association: string | null;
  reputation_score: number | null; // e.g., VirusTotal score
  reputation_source: string | null;
  cve_references: CVEReference[];
  first_seen_in_wild: string | null;
  last_updated: string;
  confidence: number; // 0-100
  ttps: string[]; // Additional TTPs from threat intel
  references: ThreatReference[];
}

export interface CVEReference {
  cve_id: string;
  description: string | null;
  cvss_score: number | null;
  severity: string | null;
  exploited_in_wild: boolean;
  patch_available: boolean;
}

export interface ThreatReference {
  title: string;
  url: string;
  source: string;
  published_date: string | null;
}

// ============================================================================
// CATEGORY 9: DEDUPLICATED ALERTS - Simplified from correlation
// Groups same alerts triggered across platforms or multiple times
// ============================================================================

export interface DuplicateAlertGroup {
  deduplication_id: string;
  total_count: number;
  unique_platforms: string[]; // e.g., ['CrowdStrike', 'Palo Alto', 'Proofpoint']
  first_seen: string;
  last_seen: string;
  time_span_minutes: number;
  lead_alert: DuplicateAlert; // The primary/first alert
  duplicates: DuplicateAlert[]; // Other instances of the same alert
}

export interface DuplicateAlert {
  alert_id: string;
  alert_name: string;
  severity: VendorSeverity;
  timestamp: string;
  source_system: string; // Platform that triggered this instance
  source_system_url: string | null; // Link back to the source platform
  summary: string | null;
}

// ============================================================================
// CATEGORY 10: TIMELINE / EVIDENCE
// ============================================================================

export interface TimelineEvent {
  id: string;
  timestamp: string;
  event_type: TimelineEventType;
  title: string;
  description: string;
  source: string | null;
  raw_log: string | null;
  analyst_id: string | null;
  analyst_name: string | null;
  is_key_event: boolean;
  artifacts: EventArtifact[];
}

export interface EventArtifact {
  id: string;
  name: string;
  type: string;
  size_bytes: number | null;
  url: string | null;
  preview: string | null;
}

// ============================================================================
// CATEGORY 11: IMPACT & REMEDIATION
// ============================================================================

export interface ImpactRemediation {
  estimated_business_impact: string | null;
  impact_severity: 'critical' | 'high' | 'medium' | 'low' | 'none' | null;
  affected_systems: string[];
  affected_data_types: string[];
  data_exposure_confirmed: boolean;
  recommended_actions: RecommendedAction[];
  actions_taken: ActionTaken[];
  resolution_summary: string | null;
  resolved_at: string | null;
  resolved_by: User | null;
}

export interface RecommendedAction {
  id: string;
  action: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  is_completed: boolean;
  assigned_to: string | null;
}

export interface ActionTaken {
  id: string;
  action: string;
  performed_at: string;
  performed_by: User | null;
  outcome: string | null;
  notes: string | null;
}

// ============================================================================
// CATEGORY 12: COMPLIANCE & AUDIT
// ============================================================================

export interface ComplianceAudit {
  compliance_frameworks: ComplianceFramework[];
  regulatory_reporting_required: boolean;
  reporting_deadline: string | null;
  reporting_status: 'not_required' | 'pending' | 'submitted' | 'acknowledged';
  audit_trail: AuditEntry[];
  retention_period_days: number | null;
  data_classification: string | null;
}

export interface ComplianceFramework {
  name: string; // e.g., 'NIS2', 'GDPR', 'ISO 27001', 'PCI-DSS'
  relevant_controls: string[];
  compliance_status: 'compliant' | 'non_compliant' | 'under_review' | 'not_applicable';
}

export interface AuditEntry {
  id: string;
  timestamp: string;
  user_id: string;
  user_name: string;
  action: string;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  ip_address: string | null;
}

// ============================================================================
// MAIN EXTENDED TICKET INTERFACE
// ============================================================================

export interface ExtendedTicket {
  // Core ticket data
  id: string;
  title: string;
  description: string;

  // Category 1: Ticket Meta
  meta: TicketMeta;

  // Category 2: Alert Core
  alert: AlertCore;

  // Category 3: Affected Identity (optional - may not always be present)
  affected_identity: AffectedIdentity | null;

  // Category 4: Affected Asset (optional)
  affected_asset: AffectedAsset | null;

  // Category 5: Network Context (optional)
  network_context: NetworkContext | null;

  // Category 6: MITRE ATT&CK (optional - array for multiple mappings)
  mitre_mappings: MitreMapping[];

  // Category 7: IOCs (variable - may be empty)
  iocs: IOC[];

  // Category 8: Threat Intelligence (variable - may be empty)
  threat_intel: ThreatIntelEnrichment[];

  // Category 9: Deduplicated Alerts (variable - groups same alerts across platforms)
  duplicate_alerts: DuplicateAlertGroup | null;

  // Category 10: Timeline
  timeline: TimelineEvent[];

  // Category 11: Impact & Remediation
  impact_remediation: ImpactRemediation | null;

  // Category 12: Compliance & Audit
  compliance: ComplianceAudit | null;

  // Raw data from source (for advanced users)
  raw_data: any | null;

  // Analyst-authored fields surfaced to the client
  client_recommendations: string | null;
  resolution_summary: string | null;

  // Comments and attachments (from existing system)
  comments_count: number;
  attachments_count: number;
}

// ============================================================================
// COMPONENT PROPS INTERFACES
// ============================================================================

export interface TicketDetailsProps {
  ticket: ExtendedTicket;
  onClose?: () => void;
  onStatusChange?: (status: TicketStatus) => void;
  onPriorityChange?: (priority: TicketPriority) => void;
  onAssigneeChange?: (assigneeId: string | null) => void;
  compactMode?: boolean;
}

// ============================================================================
// UI STATE INTERFACES
// ============================================================================

export interface TicketDetailsViewState {
  activeSection: 'overview' | 'investigation' | 'timeline' | 'remediation';
  expandedSections: Set<string>;
  densityMode: 'compact' | 'comfortable';
  showRawData: boolean;
}

// ============================================================================
// HELPER TYPES
// ============================================================================

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'informational';

export interface SeverityConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon?: string;
}

export const SEVERITY_CONFIG: Record<SeverityLevel, SeverityConfig> = {
  critical: {
    label: 'Critical',
    color: '#F87171',
    bgColor: '#3A1A1A',
    borderColor: '#5A2D2D'
  },
  high: {
    label: 'High',
    color: '#FB923C',
    bgColor: '#3A2E1A',
    borderColor: '#5A452D'
  },
  medium: {
    label: 'Medium',
    color: '#FBBF24',
    bgColor: '#3A351A',
    borderColor: '#5A4D2D'
  },
  low: {
    label: 'Low',
    color: '#4ADE80',
    bgColor: '#1A3A1A',
    borderColor: '#2D5A2D'
  },
  informational: {
    label: 'Info',
    color: '#60A5FA',
    bgColor: '#1A2E4E',
    borderColor: '#2D4A7D'
  }
};
